import { useQuery, useMutation, useSubscription, useLazyQuery } from '@apollo/client/react';
import { useCallback, useMemo } from 'react';
import { rrulestr, RRuleSet } from 'rrule';
import {
  GET_EVENTS,
  GET_EVENT,
  CREATE_EVENT,
  UPDATE_EVENT,
  DELETE_EVENT,
  UPSERT_EVENT_PARTICIPANTS,
  REMOVE_EVENT_PARTICIPANT,
  UPDATE_PARTICIPANT_STATUS,
  SUBSCRIBE_EVENTS,
} from '@common/graphql';
import { useAuth } from '../context';
import { useFamily } from '../context';
import type {
  Event,
  EventsByDay,
  DbEvent,
  CreateEventInput,
  UpdateEventInput,
  ParticipantInput,
  ParticipantStatus,
  CalendarType,
  AlertType,
  RepeatType,
  EventLocation,
} from '../types';

// DB <-> Frontend mapping utilities
function dbEventToFrontend(dbEvent: DbEvent): Event {
  const participants: Record<string, boolean> = {};
  if (dbEvent.participants) {
    for (const p of dbEvent.participants) {
      participants[p.person_id] = true;
    }
  }

  return {
    id: dbEvent.id,
    title: dbEvent.title,
    description: dbEvent.description || undefined,
    location: dbEvent.location || undefined,
    startTime: new Date(dbEvent.start_time).getTime(),
    endTime: new Date(dbEvent.end_time).getTime(),
    duration: new Date(dbEvent.end_time).getTime() - new Date(dbEvent.start_time).getTime(),
    isAllDay: dbEvent.is_all_day,
    calendar: dbEvent.calendar as CalendarType,
    attendees: participants,
    families: dbEvent.families?.map((f) => f.family_id) || [],
    notes: dbEvent.notes || undefined,
    url: dbEvent.url || undefined,
    alert: (dbEvent.alert as AlertType) || undefined,
    tags: dbEvent.tags || undefined,
    rrule: dbEvent.rrule || undefined,
    repeat: (dbEvent.repeat as RepeatType) || undefined,
    healthProvider: dbEvent.data?.healthProvider,
    activity: dbEvent.data?.activity,
    school: dbEvent.data?.school,
    caregiver: dbEvent.data?.caregiver,
    householdId: dbEvent.data?.householdId,
    custodyPlanId: dbEvent.data?.custodyPlanId,
    household: dbEvent.data?.household,
    color: dbEvent.data?.color,
    createdAt: new Date(dbEvent.created_at).getTime(),
    modifiedBy: dbEvent.created_by,
    type: 'event',
  };
}

function frontendEventToCreateInput(
  event: Partial<Event>,
  familyIds: string[],
  createdBy: string,
  participantInputs?: ParticipantInput[]
): CreateEventInput {
  const data: Record<string, any> = {};
  if (event.healthProvider) data.healthProvider = event.healthProvider;
  if (event.activity) {
    data.activity = event.activity;
    data.activityDataId = event.activity.id;
  }
  if (event.school) {
    data.school = event.school;
    if (event.school.classId) data.classDataId = event.school.classId;
  }
  if (event.caregiver) data.caregiver = event.caregiver;
  if (event.householdId) data.householdId = event.householdId;
  if (event.custodyPlanId) data.custodyPlanId = event.custodyPlanId;
  if (event.household) data.household = event.household;
  if (event.color) data.color = event.color;

  return {
    title: event.title || '',
    description: event.description,
    location:
      typeof event.location === 'string'
        ? ({ address: event.location } as EventLocation)
        : (event.location as EventLocation) || undefined,
    start_time: event.startTime ? new Date(event.startTime).toISOString() : new Date().toISOString(),
    end_time: event.endTime ? new Date(event.endTime).toISOString() : new Date().toISOString(),
    is_all_day: event.isAllDay || false,
    calendar: event.calendar || 'General',
    family_ids: familyIds,
    rrule: event.rrule,
    repeat: event.repeat,
    alert: event.alert,
    tags: event.tags,
    notes: event.notes,
    url: event.url,
    created_by: createdBy,
    data: Object.keys(data).length > 0 ? data : undefined,
    participants: participantInputs,
  };
}

function frontendEventToUpdateInput(event: Partial<Event>): UpdateEventInput {
  const result: UpdateEventInput = {};

  if (event.title !== undefined) result.title = event.title;
  if (event.description !== undefined) result.description = event.description;
  if (event.location !== undefined) {
    result.location =
      typeof event.location === 'string'
        ? ({ address: event.location } as EventLocation)
        : (event.location as EventLocation) || undefined;
  }
  if (event.startTime !== undefined) result.start_time = new Date(event.startTime).toISOString();
  if (event.endTime !== undefined) result.end_time = new Date(event.endTime).toISOString();
  if (event.isAllDay !== undefined) result.is_all_day = event.isAllDay;
  if (event.calendar !== undefined) result.calendar = event.calendar;
  if (event.rrule !== undefined) result.rrule = event.rrule;
  if (event.repeat !== undefined) result.repeat = event.repeat;
  if (event.alert !== undefined) result.alert = event.alert;
  if (event.tags !== undefined) result.tags = event.tags;
  if (event.notes !== undefined) result.notes = event.notes;
  if (event.url !== undefined) result.url = event.url;

  const data: Record<string, any> = {};
  if (event.healthProvider !== undefined) data.healthProvider = event.healthProvider;
  if (event.activity !== undefined) {
    data.activity = event.activity;
    data.activityDataId = event.activity?.id || null;
  }
  if (event.school !== undefined) {
    data.school = event.school;
    data.classDataId = event.school?.classId || null;
  }
  if (event.caregiver !== undefined) data.caregiver = event.caregiver;
  if (event.householdId !== undefined) data.householdId = event.householdId;
  if (event.custodyPlanId !== undefined) data.custodyPlanId = event.custodyPlanId;
  if (event.household !== undefined) data.household = event.household;
  if (event.color !== undefined) data.color = event.color;
  if (Object.keys(data).length > 0) result.data = data;

  return result;
}

function localDateKey(timestamp: number): string {
  const d = new Date(timestamp);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function groupEventsByDay(events: Event[]): EventsByDay {
  const byDay: EventsByDay = {};
  for (const event of events) {
    const dateKey = localDateKey(event.startTime);
    if (!byDay[dateKey]) byDay[dateKey] = [];
    byDay[dateKey].push(event);
  }
  for (const key of Object.keys(byDay)) {
    byDay[key].sort((a, b) => a.startTime - b.startTime);
  }
  return byDay;
}

function expandRecurringEvents(events: Event[], startDate: string, endDate: string): Event[] {
  const rangeStart = new Date(startDate);
  const rangeEnd = new Date(endDate + 'T23:59:59.999Z');
  const result: Event[] = [];

  for (const event of events) {
    if (!event.rrule) {
      const eventDate = new Date(event.startTime);
      if (eventDate >= rangeStart && eventDate <= rangeEnd) {
        result.push(event);
      }
      continue;
    }

    try {
      const ruleSet = rrulestr(event.rrule, { forceset: true }) as RRuleSet;
      const occurrences = ruleSet.between(rangeStart, rangeEnd, true);
      const baseDuration = event.endTime - event.startTime;

      const baseStart = new Date(event.startTime);
      const baseHours = baseStart.getHours();
      const baseMinutes = baseStart.getMinutes();
      const baseSeconds = baseStart.getSeconds();

      for (const occ of occurrences) {
        const occLocal = new Date(occ);
        const dateStr = `${occLocal.getFullYear()}-${String(occLocal.getMonth() + 1).padStart(2, '0')}-${String(occLocal.getDate()).padStart(2, '0')}`;
        const adjustedStart = new Date(occ);
        adjustedStart.setHours(baseHours, baseMinutes, baseSeconds, 0);

        const occurrenceEvent: Event = {
          ...event,
          id: `${event.id}_${dateStr}`,
          startTime: adjustedStart.getTime(),
          endTime: adjustedStart.getTime() + baseDuration,
          recurEvent: event,
          occurrenceDate: dateStr,
        };

        result.push(occurrenceEvent);
      }
    } catch (err) {
      console.warn('Failed to parse rrule for event:', event.id, err);
      const eventDate = new Date(event.startTime);
      if (eventDate >= rangeStart && eventDate <= rangeEnd) {
        result.push(event);
      }
    }
  }

  return result;
}

interface UseEventsOptions {
  startDate: string;
  endDate: string;
  skip?: boolean;
  useSubscription?: boolean;
}

interface UseEventsResult {
  events: Event[];
  eventsByDay: EventsByDay;
  loading: boolean;
  error: any;
  refetch: () => Promise<any>;
  createEvent: (event: Partial<Event>, participants?: ParticipantInput[]) => Promise<Event | null>;
  updateEvent: (id: string, event: Partial<Event>) => Promise<Event | null>;
  deleteEvent: (id: string) => Promise<boolean>;
  updateEventInstance: (
    baseEventId: string,
    occurrenceDate: string,
    changes: Partial<Event>
  ) => Promise<boolean>;
  deleteEventInstance: (baseEventId: string, occurrenceDate: string) => Promise<boolean>;
  addParticipants: (eventId: string, participants: ParticipantInput[]) => Promise<boolean>;
  removeParticipant: (eventId: string, personId: string) => Promise<boolean>;
  updateParticipantStatus: (
    eventId: string,
    personId: string,
    status: ParticipantStatus
  ) => Promise<boolean>;
}

export function useEvents(options: UseEventsOptions): UseEventsResult {
  const useRealtime = options.useSubscription === true;
  const { user } = useAuth();
  const { currentFamilyId } = useFamily();

  const variables = useMemo(() => {
    return {
      start_date: new Date(options.startDate).toISOString(),
      end_date: new Date(options.endDate + 'T23:59:59.999Z').toISOString(),
    };
  }, [options.startDate, options.endDate]);

  const {
    data: subData,
    loading: subLoading,
    error: subError,
  } = useSubscription(SUBSCRIBE_EVENTS, {
    variables: variables,
    skip: options.skip || !useRealtime,
  });

  const {
    data: queryData,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery(GET_EVENTS, {
    variables: variables,
    skip: options.skip,
    fetchPolicy: 'cache-and-network',
  });

  const data = useRealtime && (subData as any)?.event ? subData : queryData;
  const loading = useRealtime ? subLoading && queryLoading : queryLoading;
  const error = (data as any)?.event ? null : subError || queryError;

  const rawEvents = useMemo(() => {
    if (!(data as any)?.event) return [];
    return ((data as any).event as DbEvent[]).map(dbEventToFrontend);
  }, [data]);

  const events = useMemo(
    () => expandRecurringEvents(rawEvents, options.startDate, options.endDate),
    [rawEvents, options.startDate, options.endDate]
  );

  const eventsByDay = useMemo(() => groupEventsByDay(events), [events]);

  const [createMutation] = useMutation(CREATE_EVENT);
  const [updateMutation] = useMutation(UPDATE_EVENT);
  const [deleteMutation] = useMutation(DELETE_EVENT);
  const [upsertParticipantsMutation] = useMutation(UPSERT_EVENT_PARTICIPANTS);
  const [removeParticipantMutation] = useMutation(REMOVE_EVENT_PARTICIPANT);
  const [updateStatusMutation] = useMutation(UPDATE_PARTICIPANT_STATUS);
  const [fetchEventById] = useLazyQuery(GET_EVENT);

  const createEvent = useCallback(
    async (event: Partial<Event>, participants?: ParticipantInput[]): Promise<Event | null> => {
      if (!user?.id) return null;

      // Participants are required - default to current user if none provided
      // The current user's person_id needs to be passed via participants
      const effectiveParticipants = participants && participants.length > 0
        ? participants
        : [{ person_id: user.id, role: 'organizer' as const }];

      // Family IDs are now optional
      const familyIds = event.families?.length ? event.families : (currentFamilyId ? [currentFamilyId] : []);

      const input = frontendEventToCreateInput(
        event,
        familyIds,
        user.id,
        effectiveParticipants
      );

      const object: any = {
        title: input.title,
        description: input.description,
        location: input.location,
        start_time: input.start_time,
        end_time: input.end_time,
        is_all_day: input.is_all_day,
        calendar: input.calendar,
        rrule: input.rrule,
        repeat: input.repeat,
        alert: input.alert,
        tags: input.tags,
        notes: input.notes,
        url: input.url,
        created_by: input.created_by,
        data: input.data || {},
        // Participants are required - events are now participant-focused
        participants: {
          data: effectiveParticipants.map((p) => ({
            person_id: p.person_id,
            status: p.status || 'invited',
            role: p.role || 'attendee',
            notes: p.notes,
          })),
        },
      };

      // Only add families if provided (family_id is now optional)
      if (familyIds.length > 0) {
        object.families = {
          data: familyIds.map((fid) => ({ family_id: fid })),
        };
      }

      try {
        const result = await createMutation({
          variables: { object },
          refetchQueries: ['GetEvents', 'GetTodayEventCount'],
        });
        const created = (result.data as any)?.insert_event_one;
        return created ? dbEventToFrontend(created) : null;
      } catch (err) {
        console.error('Error creating event:', err);
        throw err;
      }
    },
    [currentFamilyId, user?.id, createMutation]
  );

  const updateEvent = useCallback(
    async (id: string, event: Partial<Event>): Promise<Event | null> => {
      const set = frontendEventToUpdateInput(event);
      try {
        const result = await updateMutation({
          variables: { id, set },
          refetchQueries: ['GetEvents', 'GetTodayEventCount'],
        });
        const updated = (result.data as any)?.update_event_by_pk;
        return updated ? dbEventToFrontend(updated) : null;
      } catch (err) {
        console.error('Error updating event:', err);
        throw err;
      }
    },
    [updateMutation]
  );

  const deleteEvent = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await deleteMutation({
          variables: { id },
          refetchQueries: ['GetEvents', 'GetTodayEventCount'],
        });
        return true;
      } catch (err) {
        console.error('Error deleting event:', err);
        throw err;
      }
    },
    [deleteMutation]
  );

  const addParticipants = useCallback(
    async (eventId: string, participants: ParticipantInput[]): Promise<boolean> => {
      try {
        await upsertParticipantsMutation({
          variables: {
            objects: participants.map((p) => ({
              event_id: eventId,
              person_id: p.person_id,
              status: p.status || 'invited',
              role: p.role || 'attendee',
              notes: p.notes,
            })),
          },
          refetchQueries: ['GetEvents'],
        });
        return true;
      } catch (err) {
        console.error('Error adding participants:', err);
        throw err;
      }
    },
    [upsertParticipantsMutation]
  );

  const removeParticipant = useCallback(
    async (eventId: string, personId: string): Promise<boolean> => {
      try {
        await removeParticipantMutation({
          variables: { event_id: eventId, person_id: personId },
          refetchQueries: ['GetEvents'],
        });
        return true;
      } catch (err) {
        console.error('Error removing participant:', err);
        throw err;
      }
    },
    [removeParticipantMutation]
  );

  const updateParticipantStatus = useCallback(
    async (eventId: string, personId: string, status: ParticipantStatus): Promise<boolean> => {
      try {
        await updateStatusMutation({
          variables: { event_id: eventId, person_id: personId, status },
        });
        return true;
      } catch (err) {
        console.error('Error updating participant status:', err);
        throw err;
      }
    },
    [updateStatusMutation]
  );

  const addExdateToRrule = (rrule: string, exdate: string, baseStartTime: Date): string => {
    try {
      const ruleSet = rrulestr(rrule, { forceset: true }) as RRuleSet;
      const exdateDate = new Date(exdate + 'T00:00:00Z');
      exdateDate.setUTCHours(
        baseStartTime.getUTCHours(),
        baseStartTime.getUTCMinutes(),
        baseStartTime.getUTCSeconds(),
        0
      );
      ruleSet.exdate(exdateDate);
      return ruleSet.toString();
    } catch (err) {
      console.error('[addExdateToRrule] Error parsing rrule:', err);
      const datePart = exdate.replace(/-/g, '');
      const hours = String(baseStartTime.getUTCHours()).padStart(2, '0');
      const minutes = String(baseStartTime.getUTCMinutes()).padStart(2, '0');
      const seconds = String(baseStartTime.getUTCSeconds()).padStart(2, '0');
      const exdateFormatted = `${datePart}T${hours}${minutes}${seconds}Z`;
      return rrule.includes('EXDATE:')
        ? rrule.replace(/(EXDATE:[^\n]+)/, `$1,${exdateFormatted}`)
        : `${rrule}\nEXDATE:${exdateFormatted}`;
    }
  };

  const updateEventInstance = useCallback(
    async (
      baseEventId: string,
      occurrenceDate: string,
      changes: Partial<Event>
    ): Promise<boolean> => {
      try {
        const { data: eventData } = await fetchEventById({ variables: { id: baseEventId } });
        const baseEvent = (eventData as any)?.event_by_pk;
        if (!baseEvent) {
          throw new Error('Base event not found');
        }

        const baseStartTime = new Date(baseEvent.start_time);
        const existingRrule = baseEvent.rrule || '';
        const updatedRrule = addExdateToRrule(existingRrule, occurrenceDate, baseStartTime);

        await updateMutation({
          variables: {
            id: baseEventId,
            set: { rrule: updatedRrule },
          },
        });

        const baseEndTime = new Date(baseEvent.end_time);
        const duration = baseEndTime.getTime() - baseStartTime.getTime();

        const occDate = new Date(occurrenceDate + 'T00:00:00');
        occDate.setHours(baseStartTime.getHours(), baseStartTime.getMinutes(), baseStartTime.getSeconds());
        const occEndDate = new Date(occDate.getTime() + duration);

        const newEvent: Partial<Event> = {
          title: changes.title ?? baseEvent.title,
          description: changes.description ?? baseEvent.description,
          location: changes.location ?? baseEvent.location,
          startTime: changes.startTime ?? occDate.getTime(),
          endTime: changes.endTime ?? occEndDate.getTime(),
          isAllDay: changes.isAllDay ?? baseEvent.is_all_day,
          calendar: changes.calendar ?? baseEvent.calendar,
          notes: changes.notes ?? baseEvent.notes,
          alert: changes.alert ?? baseEvent.alert,
          attendees: changes.attendees ?? {},
          families: [],
        };

        const newEventData = {
          ...baseEvent.data,
          parentEventId: baseEventId,
          originalOccurrenceDate: occurrenceDate,
        };

        const familyIds = (baseEvent.families || []).map((f: any) => f.family_id);

        await createMutation({
          variables: {
            object: {
              title: newEvent.title,
              description: newEvent.description,
              location: newEvent.location,
              start_time: new Date(newEvent.startTime!).toISOString(),
              end_time: new Date(newEvent.endTime!).toISOString(),
              is_all_day: newEvent.isAllDay,
              calendar: newEvent.calendar,
              notes: newEvent.notes,
              alert: newEvent.alert,
              created_by: baseEvent.created_by,
              data: newEventData,
              families: {
                data: familyIds.map((fid: string) => ({ family_id: fid })),
              },
            },
          },
          refetchQueries: ['GetEvents', 'GetTodayEventCount'],
        });

        return true;
      } catch (err) {
        console.error('Error updating event instance:', err);
        throw err;
      }
    },
    [fetchEventById, updateMutation, createMutation]
  );

  const deleteEventInstance = useCallback(
    async (baseEventId: string, occurrenceDate: string): Promise<boolean> => {
      try {
        const { data: eventData } = await fetchEventById({ variables: { id: baseEventId } });
        const baseEvent = (eventData as any)?.event_by_pk;
        if (!baseEvent) {
          throw new Error('Base event not found');
        }

        const baseStartTime = new Date(baseEvent.start_time);
        const existingRrule = baseEvent.rrule || '';
        const updatedRrule = addExdateToRrule(existingRrule, occurrenceDate, baseStartTime);

        await updateMutation({
          variables: {
            id: baseEventId,
            set: { rrule: updatedRrule },
          },
          refetchQueries: ['GetEvents', 'GetTodayEventCount'],
        });

        return true;
      } catch (err) {
        console.error('Error deleting event instance:', err);
        throw err;
      }
    },
    [fetchEventById, updateMutation]
  );

  return {
    events,
    eventsByDay,
    loading,
    error,
    refetch,
    createEvent,
    updateEvent,
    deleteEvent,
    updateEventInstance,
    deleteEventInstance,
    addParticipants,
    removeParticipant,
    updateParticipantStatus,
  };
}

export default useEvents;
